import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { NO_DEPARTMENT } from '../../../constants/organizations';
import { routes, URL_VARS } from '../../../constants/routing';
import { NON_CANCELING_STATUSES } from '../../../constants/tools';
import { DEPT_ADMIN, DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN, USER } from '../../../constants/users';
import { fetchReportByIdRequest } from '../../../store/actions/reports';
import { cancelEngagementByOrderIdRequest, deleteEngagementByOrderIdRequest } from '../../../store/actions/tools';
import {
  selectAdminType,
  selectIsSuperAdmin,
  selectUserDepartmentId,
  selectUserProfile,
} from '../../../store/selectors/auth';
import { selectCurrentOrganizationId } from '../../../store/selectors/organizations';
import { IReport, ReportTypesEnum } from '../../../types/reports';
import { NotificationListEnum } from '../../../types/shell';
import notification from '../../../utils/notification';
import { getClaimUrl, getReportStatusAndDate, isValidScheduledEngagement } from '../../../utils/reports';
import { handleApiError } from '../../../utils/ui';

interface IProps {
  activeTab: ReportTypesEnum;
  pageSize?: number;
  page?: number;
}

export const useReportsContextMenu = ({ activeTab, pageSize, page }: IProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<IReport | null>(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const isSuper = useSelector(selectIsSuperAdmin);
  const adminType = useSelector(selectAdminType);
  const userProfile = useSelector(selectUserProfile);
  const userDepartmentId = useSelector(selectUserDepartmentId);
  const userOrgId = useSelector(selectCurrentOrganizationId);

  const isMoreActionsDisabled = useCallback(
    (report: IReport) =>
      adminType === DEPT_ADMIN &&
      userDepartmentId !== NO_DEPARTMENT &&
      report.department_id === NO_DEPARTMENT &&
      !getClaimUrl(report) &&
      !report.tracking_url,
    [adminType, userDepartmentId],
  );

  const canCancelReport = useMemo(() => {
    const {
      org_id: engagementOrgId,
      department_id: engagementDepartmentId,
      sender_email: engagementSenderEmail,
      created_by: engagementCreatedBy,
    } = selectedItem || {};
    const { email: userEmail } = userProfile || {};

    const checkEmail = engagementSenderEmail === userEmail || engagementCreatedBy === userEmail;
    const checkDepartmentId = engagementDepartmentId === userDepartmentId;
    const checkOrgId = engagementOrgId === userOrgId;

    switch (true) {
      case adminType === USER:
        return checkEmail;
      case adminType === DEPT_ADMIN:
        return checkEmail || checkDepartmentId;
      case adminType === ORG_ADMIN:
        return checkOrgId;
      case adminType === DISTRIBUTOR:
      case adminType === SUPER_ADMIN:
        return true;
      default:
        return false;
    }
  }, [selectedItem, userProfile, userDepartmentId, userOrgId, adminType]);

  const isCurrentReportScheduled = useMemo(
    () => (selectedItem ? isValidScheduledEngagement(selectedItem) : false),
    [selectedItem],
  );

  const isNonCancelingReportStatus = useCallback(
    (status: string) => {
      if (!selectedItem) {
        return true;
      }

      const isScheduled = isValidScheduledEngagement(selectedItem);

      // scheduled sends must always be cancellable
      return NON_CANCELING_STATUSES.includes(status) && !isScheduled;
    },
    [selectedItem],
  );

  const fetchReport = useCallback(
    (reportId: string) => {
      return new Promise((resolve, reject) => {
        dispatch(fetchReportByIdRequest({ type: activeTab, reportId, resolve, reject }));
      }).catch(handleApiError(`Something bad happened. Send wasn't found.`));
    },
    [dispatch, activeTab],
  );

  const canDeleteReport = useMemo(() => {
    return Boolean(isSuper && selectedItem && !isValidScheduledEngagement(selectedItem));
  }, [isSuper, selectedItem]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, item: IReport) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedItem(item);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
  }, []);

  const handleRowClick = useCallback(
    (clickedItem: IReport | null) => {
      if (!clickedItem) {
        return;
      }
      history.push(
        routes.reports.getReportsUrl({ reportId: clickedItem?.uid, flowId: URL_VARS.SUMMARY, type: activeTab }),
      );
    },
    [activeTab, history],
  );

  const handleCancelReport = useCallback(() => {
    if (!selectedItem) {
      return;
    }
    const { order_id: orderId, uid } = selectedItem;
    const { label: status } = getReportStatusAndDate(selectedItem);

    if (isNonCancelingReportStatus(status) || !canCancelReport) {
      notification.error(NotificationListEnum.Error, { content: 'Send in this status can not be canceled' });
      return;
    }

    return new Promise((resolve, reject) => {
      const action = cancelEngagementByOrderIdRequest({
        type: isCurrentReportScheduled ? ReportTypesEnum.Scheduled : ReportTypesEnum.Regular,
        orderId,
        resolve,
        reject,
      });

      dispatch(action);
    })
      .then(() => {
        // For regular reports, refetch to update the status
        // For scheduled reports, skip refetch as they may be removed from the list
        if (!isCurrentReportScheduled) {
          return Promise.all([fetchReport(uid)]);
        }
      })
      .then(() => {
        notification.success(NotificationListEnum.Success, { content: 'This send was canceled!' });
      })
      .catch(handleApiError("Something bad happened. Send wasn't canceled."));
  }, [
    dispatch,
    fetchReport,
    isCurrentReportScheduled,
    isNonCancelingReportStatus,
    selectedItem,
    pageSize,
    page,
    canCancelReport,
  ]);

  const handleDeleteReport = useCallback(() => {
    if (!selectedItem || !canDeleteReport) {
      return;
    }
    const { order_id: orderId } = selectedItem;
    return new Promise((resolve, reject) => {
      dispatch(deleteEngagementByOrderIdRequest({ type: activeTab, orderId, resolve, reject }));
    })
      .then(() => {
        notification.success(NotificationListEnum.Success, {
          content: 'The send was successfully deleted',
          dataAction: 'engagement_delete_success',
        });
      })
      .catch(handleApiError(`Something bad happened. Send wasn't deleted.`))
      .finally(() => {
        setMenuAnchorEl(null);
      });
  }, [activeTab, canDeleteReport, dispatch, selectedItem]);

  const canCopyTrackingUrl = () => {
    return !!selectedItem?.tracking_url;
  };

  const canCopyShippingUrl = () => {
    return !!getClaimUrl(selectedItem);
  };

  return {
    menuAnchorEl,
    selectedItem,
    isNonCancelingReportStatus,
    handleMenuOpen,
    handleMenuClose,
    handleCancelReport,
    handleDeleteReport,
    canDeleteReport,
    canCancelReport,
    handleRowClick,
    canCopyTrackingUrl,
    canCopyShippingUrl,
    isMoreActionsDisabled,
  };
};
